/**
 * lib/observability.ts — VEXL Nova Observability & Telemetry Integration
 *
 * Implements a fail-safe wrapper around Sentry and PostHog.
 * If credentials are not present, functions log debug messages but prevent crash.
 */

import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { env } from './env';

// Track initialization state
let isSentryInitialized = false;
let isPostHogInitialized = false;

/**
 * Initializes Sentry error tracking and PostHog usage analytics.
 * Executed once during frontend startup.
 */
export function initObservability(): void {
  // Initialize Sentry
  if (env.VITE_SENTRY_DSN) {
    try {
      Sentry.init({
        dsn: env.VITE_SENTRY_DSN,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0,
        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        environment: import.meta.env.MODE || 'production',
      });
      isSentryInitialized = true;
    } catch (err) {
      // Silently ignore or write custom telemetry fallback
    }
  }

  // Initialize PostHog
  const posthogKey = env.VITE_POSTHOG_KEY || env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
  const posthogHost = env.VITE_POSTHOG_HOST || env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

  if (posthogKey) {
    try {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        capture_pageview: false, // Managed manually or by routing
        persistence: 'localStorage',
        autocapture: true, // Track user click events automatically
      });
      posthog.debug(false); // Force disable debug mode to clear persisted local storage flags
      isPostHogInitialized = true;
    } catch (err) {
      // Silently ignore
    }
  }
}

/**
 * Associates telemetry events and crash reports with an authenticated user context.
 */
export function setUserContext(id: string, email: string, tier: string): void {
  if (isSentryInitialized) {
    Sentry.setUser({
      id,
      email,
    });
    Sentry.setTag('tier', tier);
  }

  if (isPostHogInitialized) {
    posthog.identify(id, {
      email,
      tier,
    });
  }
}

/**
 * Clears user context from telemetry pipelines on logout.
 */
export function clearUserContext(): void {
  if (isSentryInitialized) {
    Sentry.setUser(null);
    Sentry.setTag('tier', 'none');
  }

  if (isPostHogInitialized) {
    posthog.reset();
  }
}

/**
 * Tracks a custom event with attributes in PostHog.
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (isPostHogInitialized) {
    posthog.capture(eventName, properties);
  }
}

/**
 * Captures an exception manually to Sentry with additional diagnostic context.
 */
export function captureError(error: Error | unknown, context?: Record<string, unknown>): void {
  console.error('[Observability] Error captured:', error, context);
  
  if (isSentryInitialized) {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      Sentry.captureException(error);
    });
  }
}
